// src/LightspeedTab.jsx — Settings → Lightspeed tab (Eric, 2026-09-28).
// Connection status + reconnect flow, sync job control, shop→store mapping,
// and the Store Ordering targets. Admin-only; never shows
// tokens. Self-contained like CronMonitorTab.
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Card, BlockStack, InlineStack, Text, Badge, Banner, Button, TextField,
    Select, Checkbox, DataTable, Modal, Collapsible, Tooltip, Spinner, Box,
} from '@shopify/polaris';
import { authenticatedFetch } from './AuthComponent';

// ── Helpers ────────────────────────────────────────────────────────────────

function formatLocal(iso) {
    if (!iso) return '—';
    try {
        const withZone = /[zZ]$|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`;
        const d = new Date(withZone);
        if (Number.isNaN(d.getTime())) return iso;
        return d.toLocaleString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: 'numeric', minute: '2-digit',
        });
    } catch {
        return iso;
    }
}

const JOB_LABELS = { shops: 'Shops', lookups: 'Lookups (payment types, discounts, employees, customer types, customers, gift cards)', catalog: 'Catalog', inventory: 'Inventory', inventory_log: 'Inventory log (every stock movement)', sales: 'Sales (lines, payments, customers)', cards: 'Refund cards (receipt lookup)', giftcards: 'Gift cards (load every account a payment names, balance after each payment)' };
const JOB_ORDER = ['shops', 'lookups', 'catalog', 'inventory', 'inventory_log', 'sales', 'cards', 'giftcards'];
const FULL_JOBS = new Set(['lookups', 'catalog', 'inventory']);

function JobStatusBadge({ job }) {
    if (job.running) return <Badge tone="info">Running</Badge>;
    if (job.last_status === 'ok') return <Badge tone="success">Ok</Badge>;
    if (job.last_status === 'failed') return <Badge tone="critical">Failed</Badge>;
    return <Badge>Never run</Badge>;
}

// ── Connection card ────────────────────────────────────────────────────────

// ── Admin session card (FBM vendor returns, 2026-10-13) ────────────────────
function SessionCard() {
    const [st, setSt] = useState(null);
    const [err, setErr] = useState('');
    const load = useCallback(async () => {
        try {
            const res = await authenticatedFetch('/api/settings/lightspeed/session-status');
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || res.statusText);
            setSt(data); setErr('');
        } catch (e) { setErr(e.message); }
    }, []);
    useEffect(() => { load(); }, [load]);
    const badge = !st ? null
        : !st.configured ? <Badge tone="critical">Not configured</Badge>
        : st.last_probe_ok === false ? <Badge tone="warning">Expired</Badge>
        : <Badge tone="success">Active</Badge>;
    return (
        <Card>
            <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center">
                    <BlockStack gap="100">
                        <Text variant="headingMd" as="h3">Admin session (vendor returns)</Text>
                        <Text variant="bodySm" tone="subdued">Cookies pushed by the Puppeteer warmer to POST /api/lightspeed/session. Vendor returns for Amazon FBM orders need an active session; the official API cannot create them.</Text>
                    </BlockStack>
                    <InlineStack gap="200" blockAlign="center">{badge}<Button size="slim" onClick={load}>Refresh</Button></InlineStack>
                </InlineStack>
                {err && <Banner tone="critical">{err}</Banner>}
                {st && st.configured && (
                    <BlockStack gap="100">
                        <Text as="p">Captured {formatLocal(st.captured_at)} by {st.uploaded_by || '—'} · {st.cookie_count} cookies</Text>
                        <Text as="p" tone="subdued">Validated {formatLocal(st.validated_at)} · last check {formatLocal(st.last_probe_at)} — {st.last_probe_ok === false ? 'rejected by Lightspeed; rerun the warmer' : 'ok'}</Text>
                        <Text as="p" tone="subdued">{st.user_agent_short}</Text>
                    </BlockStack>
                )}
            </BlockStack>
        </Card>
    );
}

function ConnectionCard({ status, loading, onReload, showToast }) {
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null); // {ok, msg}
    const [reconnectOpen, setReconnectOpen] = useState(false);
    const [authLoading, setAuthLoading] = useState(false);
    const [authError, setAuthError] = useState('');
    const [code, setCode] = useState('');
    const [connecting, setConnecting] = useState(false);
    const [connectError, setConnectError] = useState('');

    const token = status?.token || {};
    const configured = !!status?.configured;

    const badge = (!configured || !token.connected)
        ? <Badge tone="critical">Not connected</Badge>
        : token.expired
            ? <Badge tone="warning">Token expired — refresh pending</Badge>
            : <Badge tone="success">Connected</Badge>;

    const handleTest = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const res = await authenticatedFetch('/api/settings/lightspeed/test', { method: 'POST' });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || res.statusText);
            if (data.ok) {
                setTestResult({ ok: true, msg: `Connected — first shop: ${data.shop}` });
            } else {
                setTestResult({ ok: false, msg: data.error || 'Test failed.' });
                if (data.needs_reconnect) setReconnectOpen(true);
            }
        } catch (err) {
            setTestResult({ ok: false, msg: err.message });
        } finally {
            setTesting(false);
        }
    };

    const handleOpenAuth = async () => {
        setAuthLoading(true);
        setAuthError('');
        try {
            const res = await authenticatedFetch('/api/settings/lightspeed/authorize-url');
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || res.statusText);
            window.open(data.url, '_blank', 'noopener');
        } catch (err) {
            setAuthError(err.message);
        } finally {
            setAuthLoading(false);
        }
    };

    const handleSaveCode = async () => {
        if (!code.trim()) {
            setConnectError('Paste the authorization code or redirect URL first.');
            return;
        }
        setConnecting(true);
        setConnectError('');
        try {
            const res = await authenticatedFetch('/api/settings/lightspeed/connect', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code: code.trim() }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || res.statusText);
            showToast('ok', 'Lightspeed reconnected.');
            setCode('');
            setReconnectOpen(false);
            onReload();
        } catch (err) {
            setConnectError(err.message);
        } finally {
            setConnecting(false);
        }
    };

    if (loading) {
        return (
            <Card>
                <Box padding="400">
                    <InlineStack align="center"><Spinner accessibilityLabel="Loading connection status" size="small" /></InlineStack>
                </Box>
            </Card>
        );
    }

    return (
        <Card>
            <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center">
                    <Text variant="headingSm" as="h3">Lightspeed connection</Text>
                    {badge}
                </InlineStack>
                {!configured && (
                    <Banner tone="critical">Lightspeed credentials are missing from the server .env.</Banner>
                )}
                <BlockStack gap="100">
                    <Text variant="bodySm" tone="subdued">Account: {status?.account_id || '—'}</Text>
                    <Text variant="bodySm" tone="subdued">Token refreshed {formatLocal(token.updated_at)}</Text>
                    <Text variant="bodySm" tone="subdued">Expires {formatLocal(token.expires_at)}</Text>
                </BlockStack>
                {testResult && (
                    <Banner tone={testResult.ok ? 'success' : 'critical'} onDismiss={() => setTestResult(null)}>
                        {testResult.msg}
                    </Banner>
                )}
                <InlineStack gap="200">
                    <Button onClick={handleTest} loading={testing}>Test connection</Button>
                    <Button onClick={() => setReconnectOpen(o => !o)} disclosure={reconnectOpen ? 'up' : 'down'}>
                        Reconnect
                    </Button>
                </InlineStack>
                <Collapsible open={reconnectOpen} id="lightspeed-reconnect" transition={{ duration: '150ms', timingFunction: 'ease-out' }}>
                    <Box paddingBlockStart="200">
                        <BlockStack gap="300">
                            <Text variant="bodySm">
                                1. Open the Lightspeed authorization page and sign in.
                                {' '}2. Copy the code (or the whole address bar) from the page you land on.
                                {' '}3. Paste it here.
                            </Text>
                            {authError && <Banner tone="critical" onDismiss={() => setAuthError('')}>{authError}</Banner>}
                            <InlineStack>
                                <Button onClick={handleOpenAuth} loading={authLoading}>Open Lightspeed authorization</Button>
                            </InlineStack>
                            <TextField
                                label="Authorization code or redirect URL"
                                value={code}
                                onChange={setCode}
                                multiline={2}
                                autoComplete="off"
                            />
                            {connectError && <Banner tone="critical" onDismiss={() => setConnectError('')}>{connectError}</Banner>}
                            <InlineStack>
                                <Button variant="primary" onClick={handleSaveCode} loading={connecting}>Save code</Button>
                            </InlineStack>
                        </BlockStack>
                    </Box>
                </Collapsible>
            </BlockStack>
        </Card>
    );
}

// ── Sync card ──────────────────────────────────────────────────────────────

function SyncCard({ status, loading, onReload, showToast }) {
    const jobsByName = useMemo(() => {
        const m = {};
        (status?.jobs || []).forEach(j => { m[j.job] = j; });
        return m;
    }, [status]);
    const jobs = JOB_ORDER.map(name => jobsByName[name] || { job: name, running: false, last_status: null });
    const anyRunning = jobs.some(j => j.running);

    const [runError, setRunError] = useState('');
    const [runningJob, setRunningJob] = useState(null);
    const [confirmFull, setConfirmFull] = useState(null); // job name or null

    const runJob = async (job, full) => {
        setRunningJob(job);
        setRunError('');
        try {
            const res = await authenticatedFetch(`/api/settings/lightspeed/sync/${job}?full=${full ? 1 : 0}`, { method: 'POST' });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                if (res.status === 409) {
                    setRunError(data.detail || `${JOB_LABELS[job] || job} is already running.`);
                } else {
                    throw new Error(data.detail || res.statusText);
                }
            } else {
                showToast('ok', `${JOB_LABELS[job] || job} sync started.`);
                onReload();
            }
        } catch (err) {
            showToast('error', err.message);
        } finally {
            setRunningJob(null);
            setConfirmFull(null);
        }
    };

    if (loading) {
        return (
            <Card>
                <Box padding="400">
                    <InlineStack align="center"><Spinner accessibilityLabel="Loading sync jobs" size="small" /></InlineStack>
                </Box>
            </Card>
        );
    }

    const rows = jobs.map(j => [
        JOB_LABELS[j.job] || j.job,
        <JobStatusBadge key={`${j.job}_b`} job={j} />,
        formatLocal(j.last_run_at),
        j.rows_written == null ? '—' : Number(j.rows_written).toLocaleString('en-US'),
        j.table_total == null ? '—' : (
            <Tooltip key={`${j.job}_t`} content={j.table_detail ? Object.entries(j.table_detail).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${typeof v === 'number' ? v.toLocaleString('en-US') : (v ?? '—')}`).join(' · ') : 'Rows in the table right now'} dismissOnMouseOut>
                <span style={{ borderBottom: '1px dotted #8c9196' }}>{j.table_total.toLocaleString('en-US')}</span>
            </Tooltip>
        ),
        j.last_message
            ? (
                <Tooltip key={`${j.job}_t`} content={j.last_message}>
                    <span style={{
                        display: 'inline-block', maxWidth: 220, overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom',
                    }}
                    >
                        {j.last_message}
                    </span>
                </Tooltip>
            )
            : '—',
        j.watermark ? formatLocal(j.watermark) : '—',
        <InlineStack key={`${j.job}_a`} gap="200">
            <Button
                size="slim"
                onClick={() => runJob(j.job, false)}
                disabled={anyRunning}
                loading={runningJob === j.job}
            >
                Run now
            </Button>
            {FULL_JOBS.has(j.job) && (
                <Button size="slim" onClick={() => setConfirmFull(j.job)} disabled={anyRunning}>
                    Run full
                </Button>
            )}
        </InlineStack>,
    ]);

    return (
        <Card>
            <BlockStack gap="300">
                <Text variant="headingSm" as="h3">Sync jobs</Text>
                {runError && <Banner tone="warning" onDismiss={() => setRunError('')}>{runError}</Banner>}
                <DataTable
                    columnContentTypes={['text', 'text', 'text', 'numeric', 'numeric', 'text', 'text', 'text']}
                    headings={['Job', 'Status', 'Last run', 'Delta (last run)', 'Total rows', 'Message', 'Watermark', 'Actions']}
                    rows={rows}
                />
            </BlockStack>
            <Modal
                open={!!confirmFull}
                onClose={() => setConfirmFull(null)}
                title="Run full sync"
                primaryAction={{
                    content: 'Run now',
                    onAction: () => runJob(confirmFull, true),
                    loading: runningJob === confirmFull,
                }}
                secondaryActions={[{ content: 'Cancel', onAction: () => setConfirmFull(null) }]}
            >
                <Modal.Section>
                    <Text as="p">A full walk re-reads everything (inventory: about 90 minutes, every item). Run it now?</Text>
                </Modal.Section>
            </Modal>
        </Card>
    );
}

// ── Shop mapping card ──────────────────────────────────────────────────────

function ShopMappingCard({ showToast }) {
    const [data, setData] = useState(null); // {shops, locations, unmapped, duplicate_locations}
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [edits, setEdits] = useState({}); // shop_id -> {location_id?, b2b_customer_email?, enabled?}
    const [search, setSearch] = useState('');
    const [saving, setSaving] = useState(false);
    const [autoMapping, setAutoMapping] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await authenticatedFetch('/api/settings/lightspeed/shops');
            const json = await res.json();
            if (!res.ok) throw new Error(json.detail || res.statusText);
            setData(json);
            setEdits({});
            setError('');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const locationOptions = useMemo(() => ([
        { label: '— unmapped —', value: '' },
        ...(data?.locations || []).map(l => ({ label: l.name, value: String(l.id) })),
    ]), [data]);

    const locationNameById = useMemo(() => {
        const m = {};
        (data?.locations || []).forEach(l => { m[String(l.id)] = l.name; });
        return m;
    }, [data]);

    const origLocationId = (shop) => (shop.location_id != null ? String(shop.location_id) : '');

    const getValue = useCallback((shop, field) => {
        const e = edits[shop.shop_id];
        if (e && field in e) return e[field];
        if (field === 'location_id') return origLocationId(shop);
        if (field === 'b2b_customer_email') return shop.b2b_customer_email || '';
        return shop[field];
    }, [edits]);

    const setValue = (shop, field, value) => {
        setEdits(prev => ({ ...prev, [shop.shop_id]: { ...(prev[shop.shop_id] || {}), [field]: value } }));
    };

    const isDirty = useCallback((shop) => {
        const e = edits[shop.shop_id];
        if (!e) return false;
        if ('location_id' in e && e.location_id !== origLocationId(shop)) return true;
        if ('b2b_customer_email' in e && e.b2b_customer_email !== (shop.b2b_customer_email || '')) return true;
        if ('enabled' in e && e.enabled !== !!shop.enabled) return true;
        return false;
    }, [edits]);

    const shops = data?.shops || [];
    const dirtyCount = shops.filter(isDirty).length;

    const filteredShops = useMemo(() => {
        const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
        const filtered = words.length === 0 ? shops : shops.filter(s => {
            const locId = getValue(s, 'location_id');
            const storeName = locationNameById[locId] || '';
            const haystack = `${s.name} ${storeName}`.toLowerCase();
            return words.every(w => haystack.includes(w));
        });
        return [...filtered].sort((a, b) => {
            if (!!a.archived === !!b.archived) return 0;
            return a.archived ? 1 : -1;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shops, search, edits, locationNameById]);

    const handleSave = async () => {
        const rows = shops.filter(isDirty).map(s => {
            const locId = getValue(s, 'location_id');
            return {
                shop_id: s.shop_id,
                location_id: locId ? Number(locId) : null,
                b2b_customer_email: getValue(s, 'b2b_customer_email'),
                enabled: !!getValue(s, 'enabled'),
            };
        });
        if (rows.length === 0) return;
        setSaving(true);
        setError('');
        try {
            const res = await authenticatedFetch('/api/settings/lightspeed/shops', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ rows }),
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.detail || res.statusText);
            showToast('ok', `Saved ${json.updated ?? rows.length} shop${(json.updated ?? rows.length) === 1 ? '' : 's'}.`);
            load();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleAutoMap = async () => {
        setAutoMapping(true);
        try {
            const res = await authenticatedFetch('/api/settings/lightspeed/auto-map', { method: 'POST' });
            const json = await res.json();
            if (!res.ok) throw new Error(json.detail || res.statusText);
            showToast('ok', `${json.mapped} shop${json.mapped === 1 ? '' : 's'} mapped.`);
            load();
        } catch (err) {
            showToast('error', err.message);
        } finally {
            setAutoMapping(false);
        }
    };

    if (loading) {
        return (
            <Card>
                <Box padding="400">
                    <InlineStack align="center"><Spinner accessibilityLabel="Loading shop mapping" size="small" /></InlineStack>
                </Box>
            </Card>
        );
    }

    return (
        <Card>
            <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="start" wrap>
                    <BlockStack gap="100">
                        <Text variant="headingSm" as="h3">Shop → store mapping</Text>
                        <Text variant="bodySm" tone="subdued">
                            Every Lightspeed shop maps to one corporate Soccer Post store. The Store Ordering
                            {' '}&amp; Stats report uses this mapping; the B2B customer email is how incoming
                            {' '}B2B orders are matched to the store.
                        </Text>
                    </BlockStack>
                    <InlineStack gap="200">
                        <Button onClick={handleAutoMap} loading={autoMapping}>Auto-map unmapped</Button>
                        <Button variant="primary" onClick={handleSave} disabled={dirtyCount === 0} loading={saving}>
                            Save changes
                        </Button>
                    </InlineStack>
                </InlineStack>
                {error && <Banner tone="critical" onDismiss={() => setError('')}>{error}</Banner>}
                {data?.unmapped?.length > 0 && (
                    <Banner tone="warning">{`${data.unmapped.length} shops have no store: ${data.unmapped.join(', ')}`}</Banner>
                )}
                {data?.duplicate_locations?.length > 0 && (
                    <Banner tone="warning">{`Mapped twice: ${data.duplicate_locations.join(', ')}`}</Banner>
                )}
                <TextField
                    label="Search shops"
                    labelHidden
                    placeholder="Search by shop or store name"
                    value={search}
                    onChange={setSearch}
                    autoComplete="off"
                    clearButton
                    onClearButtonClick={() => setSearch('')}
                />
                <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text', 'text']}
                    headings={['Lightspeed shop', 'Store', 'B2B customer email', 'Enabled', '']}
                    rows={filteredShops.map(s => {
                        const dirty = isDirty(s);
                        return [
                            <BlockStack key={`${s.shop_id}_name`} gap="0">
                                <Text tone={s.archived ? 'subdued' : undefined}>{s.name}{s.archived ? ' (archived)' : ''}</Text>
                                <Text variant="bodySm" tone="subdued">{s.shop_id}</Text>
                            </BlockStack>,
                            <div key={`${s.shop_id}_loc`} style={{ minWidth: 200 }}>
                                <Select
                                    label="Store"
                                    labelHidden
                                    options={locationOptions}
                                    value={getValue(s, 'location_id')}
                                    onChange={(v) => setValue(s, 'location_id', v)}
                                />
                            </div>,
                            <div key={`${s.shop_id}_email`} style={{ minWidth: 200 }}>
                                <TextField
                                    label="B2B customer email"
                                    labelHidden
                                    type="email"
                                    autoComplete="off"
                                    value={getValue(s, 'b2b_customer_email')}
                                    onChange={(v) => setValue(s, 'b2b_customer_email', v)}
                                />
                            </div>,
                            <Checkbox
                                key={`${s.shop_id}_en`}
                                label="Enabled"
                                labelHidden
                                checked={!!getValue(s, 'enabled')}
                                onChange={(v) => setValue(s, 'enabled', v)}
                            />,
                            dirty ? <Badge key={`${s.shop_id}_dirty`} tone="attention">Changed</Badge> : '',
                        ];
                    })}
                />
            </BlockStack>
        </Card>
    );
}

// ── Ordering card ──────────────────────────────────────────────────────────

function OrderingCard({ status, loading, onReload, showToast }) {
    // Two targets (David Thomasson, 2026-10-05): a 2-week supply for east-coast stores, 3 weeks everywhere else.
    const [weeksEast, setWeeksEast] = useState('');
    const [weeksOther, setWeeksOther] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (status?.settings?.target_weeks_east != null) setWeeksEast(String(status.settings.target_weeks_east));
        if (status?.settings?.target_weeks_other != null) setWeeksOther(String(status.settings.target_weeks_other));
    }, [status?.settings?.target_weeks_east, status?.settings?.target_weeks_other]);

    const handleSave = async () => {
        const e = parseInt(weeksEast, 10), o = parseInt(weeksOther, 10);
        if (![e, o].every(n => Number.isFinite(n) && n >= 1 && n <= 52)) {
            setError('Weeks of cover must be whole numbers between 1 and 52.');
            return;
        }
        setSaving(true);
        setError('');
        try {
            const res = await authenticatedFetch('/api/settings/lightspeed', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ target_weeks_east: e, target_weeks_other: o }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || res.statusText);
            showToast('ok', 'Weeks of cover saved.');
            onReload();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Card>
                <Box padding="400">
                    <InlineStack align="center"><Spinner accessibilityLabel="Loading ordering settings" size="small" /></InlineStack>
                </Box>
            </Card>
        );
    }

    return (
        <Card>
            <BlockStack gap="300">
                <Text variant="headingSm" as="h3">Ordering suggestions</Text>
                {error && <Banner tone="critical" onDismiss={() => setError('')}>{error}</Banner>}
                <Text tone="subdued">Suggested order quantity = weekly velocity × weeks of cover − on hand − incoming, never more than B2B has. East coast = {(status?.settings?.east_coast_states || []).join(', ') || 'ME…FL'}, by the state in the Lightspeed shop name.</Text>
                <InlineStack gap="300">
                    <div style={{ maxWidth: 220 }}>
                        <TextField label="Weeks of cover, east-coast stores" type="number" min={1} max={52} value={weeksEast} onChange={setWeeksEast} autoComplete="off" />
                    </div>
                    <div style={{ maxWidth: 220 }}>
                        <TextField label="Weeks of cover, everywhere else" type="number" min={1} max={52} value={weeksOther} onChange={setWeeksOther} autoComplete="off" />
                    </div>
                </InlineStack>
                <InlineStack>
                    <Button variant="primary" onClick={handleSave} loading={saving}>Save</Button>
                </InlineStack>
            </BlockStack>
        </Card>
    );
}

// ── Main component ─────────────────────────────────────────────────────────

function LightspeedTab() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [toast, setToast] = useState(null); // {tone, msg, key}
    const toastTimer = useRef(null);

    // The app mounts Polaris without a <Frame>, so Polaris <Toast> would throw
    // "No Frame provided". Use the app's Banner-in-a-div toast (.opshub-toast),
    // same pattern as Overstock.jsx.
    const showToast = useCallback((tone, msg) => {
        setToast({ tone, msg: typeof msg === 'string' ? msg : JSON.stringify(msg), key: Date.now() });
        clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToast(null), 4000);
    }, []);

    const loadStatus = useCallback(async () => {
        try {
            const res = await authenticatedFetch('/api/settings/lightspeed');
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || res.statusText);
            setStatus(data);
            setError('');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadStatus(); }, [loadStatus]);

    // Poll while any sync job is running; stop as soon as none are.
    useEffect(() => {
        const anyRunning = (status?.jobs || []).some(j => j.running);
        if (!anyRunning) return undefined;
        const interval = setInterval(loadStatus, 15000);
        return () => clearInterval(interval);
    }, [status, loadStatus]);

    useEffect(() => () => clearTimeout(toastTimer.current), []);

    return (
        <BlockStack gap="400">
            <BlockStack gap="0">
                <Text variant="headingLg" as="h2">Lightspeed</Text>
                <Text variant="bodySm" tone="subdued">Corporate store POS integration: connection, nightly sync jobs, and the shop → store mapping the Store Ordering &amp; Stats report reads.</Text>
            </BlockStack>
            {error && <Banner tone="critical" onDismiss={() => setError('')}>{error}</Banner>}
            <ConnectionCard status={status} loading={loading} onReload={loadStatus} showToast={showToast} />
            <SessionCard />
            <SyncCard status={status} loading={loading} onReload={loadStatus} showToast={showToast} />
            <ShopMappingCard showToast={showToast} />
            <OrderingCard status={status} loading={loading} onReload={loadStatus} showToast={showToast} />
            {toast && (
                <div key={toast.key} className="opshub-toast" role="alert" aria-live="polite">
                    <Banner tone={toast.tone === 'error' ? 'critical' : 'success'} onDismiss={() => setToast(null)}>
                        <Text as="p">{toast.msg}</Text>
                    </Banner>
                </div>
            )}
        </BlockStack>
    );
}

export default LightspeedTab;
